import { CrewAgent } from "../types/crew-types";

// CDN base URL for crew agent assets
const CREW_ASSETS_CDN = 'https://assets.pagemind.ai/crew-agents';

export const crewAgents: CrewAgent[] = [
  // 1) Site Crawler Agent
  {
    id: "site-crawler",
    name: "Site Crawler",
    personalName: "Byte",
    shortDescription: "Scans websites to collect structured content",
    imagePath: "https://assets.pagemind.ai/crew-agents/crawler.jpg",
    category: ["SEO/GEO", "Content"],
    details: {
      role: "Scans the entire website to collect and update structured content.",
      generatedAssets: "Indexed page data, metadata extraction.",
      synergies: "Supplies data to Page Semantics Learner, Search Engine Analyst, Bug Finder.",
      careAndMaintenance: "Requires crawl scheduling and exclusion rules.",
      potentialEvolutions: "AI-powered prioritization of crawl depth.",
      price: "Included in basic PageMind package."
    }
  },
  // 2) Page Semantic Agent
  {
    id: "page-semantics-learner",
    name: "Page Semantics Learner",
    personalName: "Arthur",
    shortDescription: "Understands context & meaning of page content",
    imagePath: "https://assets.pagemind.ai/crew-agents/page-semantic.png",
    category: ["Content", "SEO/GEO"],
    details: {
      role: "Understands the structure, context, and meaning of page content.",
      generatedAssets: "Semantic page maps, content classification tags.",
      synergies: "Supports Suggestions with Answer, Content Recommender, SEO Optimizer.",
      careAndMaintenance: "Needs periodic retraining as site content evolves.",
      potentialEvolutions: "Automatic content enrichment suggestions.",
      price: "Included in basic PageMind package."
    }
  },
  // 3) Adjacent topics Explorer
  {
    id: "adjacent-topics-explorer",
    name: "Adjacent Topics Explorer",
    personalName: "Elena",
    shortDescription: "Identifies related topics for content expansion",
    imagePath: "https://assets.pagemind.ai/crew-agents/adjacent.jpg",
    category: ["Content", "SEO/GEO"],
    details: {
      role: "Identifies related topics to expand content reach and relevance.",
      generatedAssets: "Topic maps, content gap reports.",
      synergies: "Feeds insights to Content Recommender, SEO Optimizer.",
      careAndMaintenance: "Requires monitoring of industry trends.",
      potentialEvolutions: "AI-driven topic cluster generation.",
      price: "Included in growth package."
    }
  },
  // 4) SEO/AEO Specialist Agent
  {
    id: "seo-aeo-specialist",
    name: "SEO/AEO Specialist",
    personalName: "Sophia",
    shortDescription: "Expert SEO and Answer Engine Optimization advisor",
    imagePath: "https://assets.pagemind.ai/crew-agents/seo-specialist.jpg",
    helloVideoPath: "https://assets.pagemind.ai/crew-agents/seo-specialist-hello.mp4",
    actionVideoPath: "https://assets.pagemind.ai/crew-agents/seo-specialist-action.mp4",
    category: ["SEO/AEO", "Content"],
    details: {
      role: "Analyzes SERP and Answer Engine opportunities to provide actionable content recommendations. Optimizes for featured snippets, answer boxes, AI overviews (Google SGE, Perplexity, ChatGPT), and traditional rankings. Ensures your content is discoverable by both search engines and AI assistants.",
      generatedAssets: "SEO/AEO opportunity reports, content placement strategies, optimization recommendations, AI-ready content formats.",
      synergies: "Works with Page Semantics Learner, Adjacent Topics Explorer, Content Recommender, ChatBot for conversational insights.",
      careAndMaintenance: "Requires regular SERP tracking, AI overview monitoring, and keyword trend analysis.",
      potentialEvolutions: "Automated content generation optimized for AI engines, real-time SERP and AEO feature tracking, predictive answer engine optimization.",
      price: "Included in growth package."
    }
  },
  // 5) Customer Intent Agent
  {
    id: "customer-intent-learner",
    name: "Customer Intent Learner",
    personalName: "Oliver",
    shortDescription: "Analyzes behavior to understand user motivations",
    imagePath: "https://assets.pagemind.ai/crew-agents/customer-intent.jpg",
    category: ["Analytics", "UX"],
    details: {
      role: "Analyzes user behavior to understand purchase intent and motivations.",
      generatedAssets: "Customer intent profiles, predictive journey paths.",
      synergies: "Works with Suggestions with Answer, ChatBot, Recommender.",
      careAndMaintenance: "Needs regular input from web analytics and CRM.",
      potentialEvolutions: "Real-time intent prediction.",
      price: "Premium add-on module."
    }
  },
  // 5) Pager Chatbot Agent
  {
    id: "chatbot",
    name: "ChatBot",
    personalName: "Luna",
    shortDescription: "Engages users in conversational interactions",
    imagePath: "https://assets.pagemind.ai/crew-agents/chatbot.jpg",
    category: ["Conversation", "UX"],
    details: {
      role: "Engages users in conversational interactions to resolve queries and guide navigation.",
      generatedAssets: "Live chat threads, conversational insights.",
      synergies: "Integrates with VoiceBot, Suggestions with Answer, Customer Intent Learner.",
      careAndMaintenance: "Requires training data updates and conversation design.",
      potentialEvolutions: "Adaptive dialogue based on emotional tone.",
      price: "Premium add-on module."
    }
  },
  // 7) Voice Assistant Agent
  {
    id: "voicebot",
    name: "VoiceBot",
    personalName: "Nexus",
    shortDescription: "Provides voice-based interactions for navigation",
    imagePath: "https://assets.pagemind.ai/crew-agents/voicebot.jpg",
    category: ["Voice", "UX", "Conversation"],
    details: {
      role: "Provides voice-based interactions for site navigation and customer assistance.",
      generatedAssets: "Voice interaction logs, spoken FAQs.",
      synergies: "Works with ChatBot, Suggestions with Answer, Customer Intent Learner.",
      careAndMaintenance: "Needs voice command training and TTS tuning.",
      potentialEvolutions: "Multi-language voice support.",
      price: "Premium add-on module."
    }
  },
  // 8) Topic Research Agent
  {
    id: "topic-research",
    name: "Topic Research Agent",
    personalName: "Maxwell",
    shortDescription: "Conducts in-depth research on specific topics",
    imagePath: "https://assets.pagemind.ai/crew-agents/topic-research.jpg",
    category: ["Research", "Content"],
    details: {
      role: "Conducts in-depth research on specific topics to generate additional information to support customers' discovery and decision-making.",
      generatedAssets: "Research reports, topic insights, content recommendations.",
      synergies: "Works with Adjacent Topics Explorer, Content Recommender.",
      careAndMaintenance: "Requires regular updates to research sources.",
      potentialEvolutions: "AI-powered research synthesis.",
      price: "Included in growth package."
    }
  },
  // 9) Analytics Insights Agent / Web Analyst
  {
    id: "web-analyst",
    name: "Web Analyst",
    personalName: "Nathan",
    shortDescription: "Monitors website traffic and user behavior",
    imagePath: "https://assets.pagemind.ai/crew-agents/webanalytics.png",
    category: ["Analytics"],
    details: {
      role: "Monitors website traffic, user behavior, and engagement patterns to provide actionable insights.",
      generatedAssets: "Analytics reports, heatmaps, session replays.",
      synergies: "Feeds data to Customer Intent Learner, Competitors Analyst, and Content Recommender.",
      careAndMaintenance: "Requires integration with web analytics tools and continuous data refresh.",
      potentialEvolutions: "Predictive trend analysis, anomaly detection.",
      price: "Included in basic PageMind package."
    }
  },
  // 10) Competitors Analyst Agent
  {
    id: "competitors-analyst",
    name: "Competitors Analyst",
    personalName: "Marcus",
    shortDescription: "Tracks competitor websites for insights",
    imagePath: "https://assets.pagemind.ai/crew-agents/competitors.png",
    category: ["Competitive", "Analytics"],
    details: {
      role: "Tracks competitor websites and extracts comparative insights.",
      generatedAssets: "Competitive analysis reports, feature comparisons.",
      synergies: "Supports Benchmark Analyst, Content Recommender.",
      careAndMaintenance: "Needs competitor monitoring schedules.",
      potentialEvolutions: "AI-driven competitor alert system.",
      price: "Included in competitive intelligence package."
    }
  },
  // 10) Counter Narrative Builder
  {
    id: "counter-narrative-builder",
    name: "Counter Narrative Builder",
    personalName: "Sophia",
    shortDescription: "Builds competitive advantages through comparison insights",
    imagePath: "https://assets.pagemind.ai/crew-agents/counter-narrative.jpg",
    category: ["Content", "Competitive"],
    details: {
      role: "Builds on top of the competitors' research and prepares counter-arguments to explain to customers how the products and services on this site are better than competitors.",
      generatedAssets: "Counter-narrative content, response playbooks.",
      synergies: "Collaborates with Customer Intent Learner, Competitors Analyst.",
      careAndMaintenance: "Requires continuous monitoring of competitor content and offerings.",
      potentialEvolutions: "Automated competitive advantage detection.",
      price: "Premium brand safety module."
    }
  },
  // 11) Page Narrator
  {
    id: "narrative-presentation",
    name: "Page Narrator",
    personalName: "William",
    shortDescription: "Reads aloud page content for accessibility",
    imagePath: "https://assets.pagemind.ai/crew-agents/narrator.png",
    category: ["Voice", "Content"],
    details: {
      role: "Reads aloud page content to improve accessibility and engagement.",
      generatedAssets: "Narrated page versions, voice interaction logs.",
      synergies: "Collaborates with VoiceBot, SEO Optimizer.",
      careAndMaintenance: "Needs updated text-to-speech content synchronization.",
      potentialEvolutions: "Emotionally adaptive narration.",
      price: "Premium accessibility module."
    }
  },
  // 12) Hands off navigator
  {
    id: "hands-off-navigation",
    name: "Hands-off Navigation",
    personalName: "Isabella",
    shortDescription: "Guides users through websites automatically",
    imagePath: "https://assets.pagemind.ai/crew-agents/handsoff.jpg",
    category: ["UX"],
    details: {
      role: "Guides users through websites automatically without manual clicks.",
      generatedAssets: "Navigation flows, smart path suggestions.",
      synergies: "Integrates with VoiceBot, Content Recommender.",
      careAndMaintenance: "Requires continuous mapping of site structure.",
      potentialEvolutions: "Predictive navigation flows based on user persona.",
      price: "Included in growth package."
    }
  },
  // 13) Product Comparison Agent
  {
    id: "product-comparison",
    name: "Product Comparison Agent",
    personalName: "Ava",
    shortDescription: "Compares products and features intelligently",
    imagePath: "https://assets.pagemind.ai/crew-agents/product-comparison.jpg",
    category: ["Content", "UX"],
    details: {
      role: "Analyzes and compares products across your site to provide intelligent recommendations.",
      generatedAssets: "Product comparison tables, feature matrices.",
      synergies: "Works with Content Recommender, Customer Intent Learner.",
      careAndMaintenance: "Requires product catalog synchronization.",
      potentialEvolutions: "AI-powered comparison insights.",
      price: "Premium e-commerce module."
    }
  },
  // 14) Question Suggester
  {
    id: "question-suggestion",
    name: "Suggestions with Answer",
    personalName: "Emma",
    shortDescription: "Provides ready-to-use answers for common questions",
    imagePath: "https://assets.pagemind.ai/crew-agents/suggestions-with-answers.jpg",
    category: ["Content", "UX"],
    details: {
      role: "Provides proactive suggestions with ready-to-use answers for common questions.",
      generatedAssets: "FAQ responses, inline suggestion cards.",
      synergies: "Collaborates with ChatBot, Customer Intent Learner.",
      careAndMaintenance: "Needs regular updates of answer database.",
      potentialEvolutions: "Contextual suggestions based on user behavior.",
      price: "Included in standard PageMind package."
    }
  },
  // 16) Know Your Customer Surveys Agent
  {
    id: "customer-surveys",
    name: "Know Your Customer Surveys Agent",
    personalName: "Mia",
    shortDescription: "Creates and analyzes customer surveys",
    imagePath: "https://assets.pagemind.ai/crew-agents/kyc-survey.jpg",
    category: ["Research", "Analytics"],
    details: {
      role: "Designs intelligent surveys to understand customer needs and preferences.",
      generatedAssets: "Survey forms, response analytics, insight reports.",
      synergies: "Feeds data to Customer Intent Learner, Web Analyst.",
      careAndMaintenance: "Requires survey design optimization.",
      potentialEvolutions: "Adaptive survey questions based on responses.",
      price: "Premium research module."
    }
  },
  // 17) Replay analyst: friction finder
  {
    id: "replay-analyst",
    name: "Replay Analyst: Friction Finder",
    personalName: "Chloe",
    shortDescription: "Analyzes session replays to identify friction points",
    imagePath: "https://assets.pagemind.ai/crew-agents/replay-analyst.jpg",
    category: ["Usability", "Analytics"],
    details: {
      role: "Reviews user session replays to identify pain points and friction in the user journey.",
      generatedAssets: "Friction reports, session highlights, improvement recommendations.",
      synergies: "Works with Bug Finder, Web Analyst, Customer Intent Learner.",
      careAndMaintenance: "Requires session recording infrastructure.",
      potentialEvolutions: "Automated friction pattern detection.",
      price: "Premium UX optimization module."
    }
  },
  // 18) Behavior Analyst
  {
    id: "behavior-analyst",
    name: "Behavior Analyst",
    personalName: "Ethan",
    shortDescription: "Analyzes user behavior patterns",
    imagePath: "https://assets.pagemind.ai/crew-agents/behavior-analyst.jpg",
    category: ["Analytics", "UX"],
    details: {
      role: "Studies user behavior patterns to provide actionable insights for optimization.",
      generatedAssets: "Behavior pattern reports, user flow diagrams, optimization recommendations.",
      synergies: "Collaborates with Web Analyst, Customer Intent Learner, Replay Analyst.",
      careAndMaintenance: "Requires continuous behavior data collection.",
      potentialEvolutions: "Predictive behavior modeling.",
      price: "Premium analytics module."
    }
  },
  // 19) Customer Segmentation Agent
  {
    id: "customer-segmentation",
    name: "Customer Segmentation Agent",
    personalName: "Daniel",
    shortDescription: "Identifies and analyzes distinct customer groups",
    imagePath: "https://assets.pagemind.ai/crew-agents/customer-segmentation-agent.jpg",
    category: ["Analytics", "UX"],
    details: {
      role: "Transforms your customer base into actionable segments with AI-powered analysis.",
      generatedAssets: "Customer segment profiles, behavioral clusters, value-based groupings, targeting strategies.",
      synergies: "Works with Customer Intent Learner, Web Analyst, Content Recommender, and Behavior Analyst.",
      careAndMaintenance: "Requires regular customer data updates and segmentation model refinement.",
      potentialEvolutions: "Real-time dynamic segmentation, predictive lifetime value modeling, automated campaign generation.",
      price: "Premium analytics module."
    }
  },
  // 20) Segment Research Agent
  {
    id: "segment-research",
    name: "Segment Research Agent",
    personalName: "Evelyn",
    shortDescription: "Conducts deep research on customer segments",
    imagePath: "https://assets.pagemind.ai/crew-agents/segment-research-agent.jpg",
    category: ["Research", "Analytics"],
    details: {
      role: "Performs in-depth research on identified customer segments to uncover insights, preferences, and behavioral patterns.",
      generatedAssets: "Segment research reports, persona documentation, preference analysis, behavioral insights, engagement strategies.",
      synergies: "Works closely with Customer Segmentation Agent, Customer Intent Learner, Topic Research Agent, and Content Recommender.",
      careAndMaintenance: "Requires regular updates to research methodologies and continuous segment data analysis.",
      potentialEvolutions: "AI-powered persona generation, predictive segment behavior modeling, automated content personalization strategies.",
      price: "Premium analytics module."
    }
  },
  // 21) Site Sector Analyzer
  {
    id: "site-sector-analyzer",
    name: "Site Sector Analyzer",
    personalName: "Theodore",
    shortDescription: "Automatically assigns sectors to sites",
    imagePath: "https://assets.pagemind.ai/crew-agents/site-sector-classifier.jpg",
    category: ["SEO/GEO", "Content"],
    details: {
      role: "Analyzes site content to automatically assign appropriate business sectors.",
      generatedAssets: "Sector classifications, industry tags, categorization reports.",
      synergies: "Supports SEO Optimizer, Content Recommender, Topic Research Agent.",
      careAndMaintenance: "Requires sector taxonomy updates.",
      potentialEvolutions: "Multi-sector classification with confidence scores.",
      price: "Included in enterprise package."
    }
  },
  // 22) Growth Analyst
  {
    id: "growth-analyst",
    name: "Growth Analyst",
    personalName: "Abigail",
    shortDescription: "Analyzes growth metrics and opportunities",
    imagePath: "https://assets.pagemind.ai/crew-agents/growth-analyst.jpg",
    category: ["Analytics"],
    details: {
      role: "Analyzes growth metrics and provides insights to scale your business.",
      generatedAssets: "Growth reports, opportunity analysis, scaling recommendations.",
      synergies: "Collaborates with Web Analyst, Behavior Analyst, Marketing Optimization Agent.",
      careAndMaintenance: "Requires access to comprehensive business metrics.",
      potentialEvolutions: "Predictive growth modeling with AI-driven recommendations.",
      price: "Premium growth module."
    }
  },
  // 23) Auto-evaluator Agent
  {
    id: "auto-evaluator",
    name: "Auto-evaluator Agent",
    personalName: "Charlotte",
    shortDescription: "Evaluates agent performance",
    imagePath: "https://assets.pagemind.ai/crew-agents/auto-evaluator.jpg",
    category: ["Analytics"],
    details: {
      role: "Evaluates and monitors the performance of all PageMind agents.",
      generatedAssets: "Performance metrics, efficiency reports, optimization recommendations.",
      synergies: "Works with all agents to monitor and improve system performance.",
      careAndMaintenance: "Requires continuous monitoring and metric threshold adjustments.",
      potentialEvolutions: "Self-optimizing agent orchestration based on performance data.",
      price: "Included in enterprise package."
    }
  },
  // 24) Insights Hub Agent
  {
    id: "insights-hub",
    name: "Insights Hub Agent",
    personalName: "Alexander",
    shortDescription: "Aggregates insights from multiple agents",
    imagePath: "https://assets.pagemind.ai/crew-agents/insight-hub.jpg",
    category: ["Analytics"],
    details: {
      role: "Central hub that aggregates and synthesizes insights from all active agents.",
      generatedAssets: "Unified insight dashboards, cross-agent analytics, executive summaries.",
      synergies: "Connects with every agent to create holistic business intelligence.",
      careAndMaintenance: "Requires regular synchronization with all agent outputs.",
      potentialEvolutions: "AI-driven insight correlation and predictive analytics.",
      price: "Premium analytics module."
    }
  },
  // 25) Intent-based Recommender
  {
    id: "intent-based-recommender",
    name: "Intent-based Recommender",
    personalName: "Olivia",
    shortDescription: "Provides intent-based recommendations",
    imagePath: "https://assets.pagemind.ai/crew-agents/intent-based-recommender.jpg",
    category: ["Content", "UX"],
    details: {
      role: "Provides personalized recommendations based on detected user intent.",
      generatedAssets: "Recommendation feeds, personalization profiles, engagement metrics.",
      synergies: "Works closely with Customer Intent Learner, Content Recommender, ChatBot.",
      careAndMaintenance: "Requires intent model updates and recommendation algorithm tuning.",
      potentialEvolutions: "Real-time recommendation adaptation using reinforcement learning.",
      price: "Premium personalization module."
    }
  },
  // 26) Keyword Extractor Agent
  {
    id: "keyword-extractor",
    name: "Keyword Extractor Agent",
    personalName: "Sofia",
    shortDescription: "Extracts keywords from content",
    imagePath: "https://assets.pagemind.ai/crew-agents/keyword-extractor.jpg",
    category: ["SEO/GEO", "Content"],
    details: {
      role: "Extracts relevant keywords and key phrases from page content for SEO and content optimization.",
      generatedAssets: "Keyword lists, density reports, SEO optimization suggestions.",
      synergies: "Supports Page Semantics Learner, SEO Optimizer, Content Recommender.",
      careAndMaintenance: "Requires keyword relevance algorithm updates.",
      potentialEvolutions: "Semantic keyword clustering and intent-based keyword mapping.",
      price: "Included in basic PageMind package."
    }
  },
  // 27) Page Role Agent
  {
    id: "page-role",
    name: "Page Role Agent",
    personalName: "Victoria",
    shortDescription: "Determines page purposes and roles",
    imagePath: "https://assets.pagemind.ai/crew-agents/page-role.jpg",
    category: ["Content", "SEO/GEO"],
    details: {
      role: "Analyzes web pages to determine their primary function and purpose (homepage, product page, contact form, blog post, etc.).",
      generatedAssets: "Page classification data with confidence scores, functional role mappings, and page hierarchy relationships.",
      synergies: "Feeds semantic analysis agents, enables chatbots to understand page context, and helps navigation agents guide users appropriately.",
      careAndMaintenance: "Monitor classification accuracy, update role taxonomy as new page types emerge, and retrain on misclassified pages.",
      potentialEvolutions: "Multi-role detection for complex pages, industry-specific role taxonomies, and ML model fine-tuning based on client feedback.",
      price: "Included in basic PageMind package."
    }
  },
  // 28) Session Summarizer Agent
  {
    id: "session-summarizer",
    name: "Session Summarizer Agent",
    personalName: "Grace",
    shortDescription: "Summarizes user sessions",
    imagePath: "https://assets.pagemind.ai/crew-agents/session-summarizer.jpg",
    category: ["Analytics", "Content"],
    details: {
      role: "Analyzes completed chat conversations to create actionable summaries for continuity and marketing insights.",
      generatedAssets: "Conversation summaries, user intent classifications, conversion signals/barriers, and personalized follow-up recommendations.",
      synergies: "Feeds customer profile builder, enables chatbot memory across sessions, provides marketing teams with conversion insights.",
      careAndMaintenance: "Monitor OpenAI API usage, ensure timely processing of chat sessions, and maintain summary quality standards.",
      potentialEvolutions: "Real-time summarization during conversations, sentiment trend analysis, automated follow-up campaign triggers, and multi-language support.",
      price: "Premium analytics module."
    }
  },
  // 29) Style Suggestion Agent
  {
    id: "style-suggestion",
    name: "Style Suggestion Agent",
    personalName: "Lily",
    shortDescription: "Provides styling suggestions",
    imagePath: "https://assets.pagemind.ai/crew-agents/style-suggestion.jpg",
    category: ["UX", "Content"],
    details: {
      role: "Analyzes website design patterns to suggest compatible UI overlay styles that match the site's aesthetic.",
      generatedAssets: "Color schemes, typography settings, border radius values, shadow styles, and design token recommendations.",
      synergies: "Powers chatbot UI customization, ensures consistent branding across PageMind widgets, and feeds visual design preferences to all overlay components.",
      careAndMaintenance: "Update design trend detection, monitor style extraction accuracy, and maintain compatibility with new CSS frameworks.",
      potentialEvolutions: "Real-time style adaptation, dark mode detection and matching, accessibility compliance checks, and brand guideline extraction.",
      price: "Included in standard PageMind package."
    }
  },
  // 30) Chatbot Intent Classifier
  {
    id: "chatbot-intent-classifier",
    name: "Chatbot Intent Classifier",
    personalName: "David",
    shortDescription: "AI-powered intent analysis for chatbot conversations",
    imagePath: "https://assets.pagemind.ai/crew-agents/chatbot-intent-classifier.jpg",
    category: ["Analytics", "Conversation"],
    details: {
      role: "Analyzes chatbot messages using OpenAI to classify user intents, categories, and extract entities with semantic understanding beyond keyword matching.",
      generatedAssets: "Intent classifications, confidence scores, entity extractions, intent patterns, and category distributions.",
      synergies: "Feeds data to Quality Scorer, Content Gap Detector, Conversation Flow Analyzer, and enables better chatbot responses.",
      careAndMaintenance: "Monitor API costs, track classification accuracy, review low-confidence classifications, and update intent taxonomy.",
      potentialEvolutions: "Multi-intent detection, custom intent training, real-time classification during conversations, and industry-specific intent models.",
      price: "Included in analytics package."
    }
  },
  // 31) Chatbot Quality Scorer
  {
    id: "chatbot-quality-scorer",
    name: "Chatbot Quality Scorer",
    personalName: "Maya",
    shortDescription: "Evaluates chatbot response quality using AI",
    imagePath: "https://assets.pagemind.ai/crew-agents/chatbot-quality-scorer.jpg",
    category: ["Analytics", "Conversation"],
    details: {
      role: "Uses AI to score chatbot responses on relevance, helpfulness, accuracy, and completeness, identifying low-quality interactions for improvement.",
      generatedAssets: "Quality scores, scoring explanations, low-quality response alerts, quality trends, and improvement recommendations.",
      synergies: "Works with Intent Classifier to understand context, feeds Content Gap Detector, and helps improve chatbot training.",
      careAndMaintenance: "Review scoring criteria, monitor score distributions, validate low-quality flagging, and adjust thresholds.",
      potentialEvolutions: "Domain-specific quality models, automated response improvement suggestions, sentiment-aware scoring, and quality prediction.",
      price: "Included in analytics package."
    }
  },
  // 32) Chatbot Content Gap Detector
  {
    id: "chatbot-content-gap-detector",
    name: "Chatbot Content Gap Detector",
    personalName: "Sebastian",
    shortDescription: "Identifies knowledge gaps in chatbot responses",
    imagePath: "https://assets.pagemind.ai/crew-agents/chatbot-content-gap-detector.jpg",
    category: ["Analytics", "Content"],
    details: {
      role: "Analyzes low-quality chatbot interactions to identify missing content, suggest improvements, and track frequently unresolved queries.",
      generatedAssets: "Content gap reports, missing topic lists, suggested content additions, gap frequency tracking, and knowledge base recommendations.",
      synergies: "Uses Quality Scorer data, collaborates with Intent Classifier for context, and feeds Content Recommender for site improvements.",
      careAndMaintenance: "Review gap identification accuracy, prioritize content additions, monitor gap resolution, and track improvement impact.",
      potentialEvolutions: "Automated content generation for gaps, proactive gap prediction, competitor gap analysis, and multi-language gap detection.",
      price: "Premium analytics module."
    }
  },
  // 33) Chatbot Conversation Flow Analyzer
  {
    id: "chatbot-conversation-flow-analyzer",
    name: "Chatbot Conversation Flow Analyzer",
    personalName: "Rafael",
    shortDescription: "Maps and analyzes chatbot conversation patterns",
    imagePath: "https://assets.pagemind.ai/crew-agents/chatbot-conversation-flow-analyzer.jpg",
    category: ["Analytics", "UX"],
    details: {
      role: "Tracks conversation flows through intent sequences, identifies common paths, abandonment points, and successful conversion patterns.",
      generatedAssets: "Flow diagrams, pattern frequencies, abandonment analysis, completion rates, conversion funnels, and journey optimization insights.",
      synergies: "Uses Intent Classifier sequences, correlates with Quality Scorer, and helps optimize chatbot conversation design.",
      careAndMaintenance: "Update flow detection algorithms, monitor pattern changes, track new conversation paths, and validate insights.",
      potentialEvolutions: "Predictive flow optimization, real-time flow guidance, personalized conversation paths, and A/B flow testing.",
      price: "Premium analytics module."
    }
  },
  // 34) Chatbot Analytics Orchestrator
  {
    id: "chatbot-analytics-agent",
    name: "Chatbot Analytics Orchestrator",
    personalName: "Magnus",
    shortDescription: "Coordinates the chatbot analytics pipeline",
    imagePath: "https://assets.pagemind.ai/crew-agents/chatbot-analytics-agent.jpg",
    category: ["Analytics", "Conversation"],
    details: {
      role: "Master agent that orchestrates the entire chatbot analytics pipeline, coordinating intent classification, quality scoring, gap detection, and flow analysis.",
      generatedAssets: "Processing reports, pipeline status, batch summaries, error logs, performance metrics, and orchestration insights.",
      synergies: "Manages all chatbot analytics agents, ensures data consistency, handles error recovery, and optimizes processing efficiency.",
      careAndMaintenance: "Monitor pipeline health, manage processing queues, handle agent failures, and optimize batch sizes.",
      potentialEvolutions: "Real-time stream processing, intelligent workload distribution, self-healing pipelines, and adaptive processing strategies.",
      price: "Included in analytics package."
    }
  },
  // 35) Natural Language Search Agent
  {
    id: "natural-language-search-agent",
    name: "Natural Language Search Agent",
    personalName: "Jasper",
    shortDescription: "Finds products using natural language queries",
    imagePath: "https://assets.pagemind.ai/crew-agents/natural-language-search-agent.jpg",
    category: ["Content", "UX", "Analytics"],
    details: {
      role: "Enables users to search for products across the entire catalog using natural language queries, understanding context, intent, and complex criteria.",
      generatedAssets: "Search results with relevance scores, query interpretations, faceted filters, product comparisons, and search analytics.",
      synergies: "Works with ChatBot for conversational search, Product Comparison Agent for detailed analysis, and Customer Intent Learner for query understanding.",
      careAndMaintenance: "Requires regular embedding updates when products change, search relevance tuning, and query performance optimization.",
      potentialEvolutions: "Multi-modal search (text + image), personalized search results, voice search integration, and predictive query suggestions.",
      price: "Premium search module."
    }
  }
];

export const allCategories: string[] = [
  "All",
  "Analytics", 
  "Content", 
  "SEO/GEO", 
  "Conversation", 
  "Voice", 
  "UX", 
  "Competitive",
  "Research",
  "Usability"
];